import { CustomTooltip } from 'app/components/Common/Tooltips';
import { isWaitingGoodToGo, placementStatusOptions } from 'app/components/Placement/NextPlacementStatus/StatusDefaults';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { IRequirementRowPadding } from 'app/models/Placement/PlacementSummary';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useRequirementFilter } from '../../../Helpers/requirementFilter';
import {
  selectEditableFields,
  selectEditableRequirementTypes,
  selectOfferInfo,
  selectBookingInfo,
} from '../../../../../../../../../store/redux-store/placement-summary/selectors';
import { OfferConfirmationCheckbox } from './OfferConfirmationCheckbox';
import { ConfirmationsRow } from '../../TableComponents/Confirmations/ConfirmationsTable';
import { PlacementOrderType } from 'app/models/Placement/PlacementDetails';
import { selectPlacementOrderType } from 'store/redux-store/placement-details/selectors';
import { selectUser } from 'oidc/user.selectors';
import { Authorized } from 'oidc/userHelper';
import { isReadOnly, userRoles } from 'oidc/userRoles';
import { TransportationTypes } from '../Transportation/TransportationWrapper';
import { requirementType } from 'app/models/Placement/PlacementSummary';
import { useArrivedFieldsetMeta } from '../useArrivedFieldsetMeta';

export const TravelConfirmed = ({ hasRequirements }: IRequirementRowPadding) => {
  const { userInfo } = useSelector(selectUser);
  const { t } = useTranslation();
  const { travelConfirmed: initialValue } = useSelector(selectEditableFields);
  const { requirementFilter } = useSelector(selectOfferInfo);
  const { control, setValue } = useFormContext();
  const { travelConfirmed: travelConfirmedReqType } = useSelector(selectEditableRequirementTypes);
  const { statusChangeSource, selectedStatus, availablePlacementStatus } = useSelector(selectPlacementStatus);
  const dispatch = useDispatch();
  const placementOrderType = useSelector(selectPlacementOrderType);
  const isStrike = placementOrderType === PlacementOrderType.StrikeOrderInHsg;
  const { currentPlacementStatus } = useSelector(selectPlacementStatus); 
  const {
    confirmation: { arrived },
  } = useSelector(selectBookingInfo);
  const arrivedFieldsetMeta = useArrivedFieldsetMeta();
  const { display } = useRequirementFilter(
    'offer-confirmation',
    requirementFilter,
    travelConfirmedReqType?.requirement,
  );
  const initialChecked = initialValue?.checked;
  const currentChecked = useWatch({
    name: 'travelConfirmed.checked', // The name of the field to watch
    defaultValue: initialValue?.checked, // Provide the default value
  });
  const datesConfirmedChecked = useWatch({ 
    name: 'datesConfirmed',
    defaultValue: { checked: false } 
  });
  const selectedTransport = useWatch({
    name: 'transportationChosen',
  });

  const { departureItineraryLink } = useSelector(selectEditableRequirementTypes);
  const canBypassItineraryCheck = Authorized([userRoles.amieTeam, userRoles.recruitment_Leadership], userInfo);

  const enableTravelConfirmedCheckBox = () => {
    if (arrivedFieldsetMeta.disabled) {
      return false;
    }

    // Disable if transportation is Unknown
    if (selectedTransport?.transportationId === TransportationTypes.UNKNOWN) {
      return false;
    }

    // disable Travel Confirmed checkbox for non-strike orders
    if (!isStrike) {
      return false;
    }

    // disable Travel Confirmed checkbox if Dates Confirmed is not checked
    if (!datesConfirmedChecked.checked) {
      return false;
    }

    // disable Travel Confirmed checkbox if itinerary is missing for Fly transportation (unless user has bypass role)
    if (
      selectedTransport?.transportationId === TransportationTypes.FLY &&
      departureItineraryLink?.requirement === requirementType.fail &&
      !canBypassItineraryCheck
    ) {
      return false;
    }

    const beyondTravelerAccepts = 
      currentPlacementStatus.activityStatusId >= placementStatusOptions.travelerAccepts.activityStatusId;

    // enable Travel Confirmed checkbox if it's currently checked, placement is beyond traveler accepts and the user has the AMIE Team/Recruitment Leadership role
    if (
      currentChecked && 
      beyondTravelerAccepts && 
      Authorized([userRoles.recruitment_Leadership], userInfo)
    ) {
      return true;
    }

    // enable Travel Confirmed checkbox if it's not currently checked, placement is beyond traveler accepts and the user does not have the read only role
    if (
      !currentChecked &&
      beyondTravelerAccepts &&
      (userInfo?.roles != null && !isReadOnly(userInfo?.roles))
    ) {
      return true;
    }

    // disable Travel Confirmed checkbox if placement hasn't reached traveler accepts
    if (!beyondTravelerAccepts) {
      return false;
    }

    return true;
  }

  const isTravelCheckboxEnabled = enableTravelConfirmedCheckBox();

  React.useEffect(() => {
    // Reset travelConfirmed to initial value when status change dialog cancel button is pressed
    if (selectedStatus === undefined && statusChangeSource === 'travelConfirmed') {
      setValue('travelConfirmed', initialValue);
      dispatch(placementStatusAction.setSelectedStatus({ status: undefined, statusChangeSource: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, selectedStatus, statusChangeSource]);

  return display ? (
    <Controller
      control={control}
      name="travelConfirmed"
      render={({ field: { onChange, ...rest } }) => (
        <ConfirmationsRow
          id="placement-offer-confirmations-travel-confirmed"
          lastUpdatedBy={rest?.value?.lastUpdatedBy}
          lastUpdatedDate={rest?.value?.lastUpdatedAt}
          requirement={rest?.value?.checked ? requirementType.pass : travelConfirmedReqType?.requirement}
          hasOtherRequirements={hasRequirements}
          controlComponent={
            <>
              <CustomTooltip
                standardMargin
                isStandardContent
                disabled={!travelConfirmedReqType?.requiredDependency || datesConfirmedChecked?.checked}
                tooltipContent={t('placement.profile.summary.offer.confirmations.line1')}
              >
                <OfferConfirmationCheckbox
                  label={t('placement.profile.summary.offer.confirmations.travelConfirmed')}
                  defaultState={initialValue}
                  disabled={!isTravelCheckboxEnabled}
                  onChange={data => {
                    if (data?.checked && availablePlacementStatus.findIndex(item => isWaitingGoodToGo(item)) !== -1) {
                      dispatch(
                        placementStatusAction.setSelectedStatus({
                          status: placementStatusOptions.waitingGoodToGo,
                          statusChangeSource: 'travelConfirmed',
                        }),
                      );
                    }
                    onChange(data);
                  }}
                  {...rest}
                />
              </CustomTooltip>
            </>
          }
        />
      )}
    />
  ) : null;
};
